import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import { STRINGS } from "@/constants/strings";
import {
  clinicalAssignmentMutationErrorMessage,
  type ClinicalAssignmentMutationKind,
} from "@/lib/clinical-assignment-api-errors";

const API_ERROR_MESSAGES: Record<number, string> = {
  400: STRINGS.api.error400,
  401: STRINGS.api.error401,
  403: STRINGS.api.error403,
  404: STRINGS.api.error404,
  409: STRINGS.api.error409,
  422: STRINGS.api.error422,
  429: STRINGS.api.error429,
  500: STRINGS.api.error500,
  503: STRINGS.api.error503,
};

function getClinicalAssignmentMutationKind(
  meta: unknown,
): ClinicalAssignmentMutationKind | undefined {
  if (!meta || typeof meta !== "object") return undefined;
  const k = (meta as { clinicalAssignmentMutation?: ClinicalAssignmentMutationKind })
    .clinicalAssignmentMutation;
  return k === "create" || k === "delete" ? k : undefined;
}

function createMutationCache() {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (error instanceof ApiError) {
        const kind = getClinicalAssignmentMutationKind(mutation.options.meta);
        if (kind) {
          const clinicalMsg = clinicalAssignmentMutationErrorMessage(error, kind);
          if (clinicalMsg) {
            toast.error(clinicalMsg);
            return;
          }
        }
        const message =
          API_ERROR_MESSAGES[error.status] ??
          `${STRINGS.api.errorGeneric} (${error.status})`;
        toast.error(message);
      } else {
        toast.error(STRINGS.api.errorGeneric);
      }
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        mutationCache: createMutationCache(),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
